import {
  ITEM_HOTBAR_AUTO_USE_COOLDOWN_MS,
  ITEM_HOTBAR_SLOT_COUNT,
  ITEM_HOTBAR_USE_COOLDOWN_MS,
  type HomeEconomy,
} from "@/home/economy";
import {
  HEALTH_POTION_TIERS,
  healthPotionCount,
  stashItemIdForTier,
  tierFromStashItemId,
} from "@/home/health-potions";
import {
  MANA_POTION_TIERS,
  manaPotionCount,
  stashItemIdForManaTier,
  tierFromManaStashItemId,
} from "@/home/mana-potions";
import type { StashItemId } from "@/home/home-stash-modal";

let lastItemHotbarUseAt = 0;
let lastItemHotbarUseCooldownMs = ITEM_HOTBAR_USE_COOLDOWN_MS;
let lastAutoItemHotbarUseAt = 0;

export function itemHotbarUseCooldownRemainingMs(now = Date.now()): number {
  return Math.max(0, lastItemHotbarUseCooldownMs - (now - lastItemHotbarUseAt));
}

export function itemHotbarUseReady(now = Date.now()): boolean {
  return itemHotbarUseCooldownRemainingMs(now) <= 0;
}

export function itemHotbarAutoUseReady(now = Date.now()): boolean {
  return now - lastAutoItemHotbarUseAt >= ITEM_HOTBAR_AUTO_USE_COOLDOWN_MS;
}

export function markItemHotbarUsed(
  now = Date.now(),
  cooldownMs = ITEM_HOTBAR_USE_COOLDOWN_MS,
): void {
  lastItemHotbarUseAt = now;
  lastItemHotbarUseCooldownMs = cooldownMs;
}

export function markItemHotbarAutoUsed(now = Date.now()): void {
  lastAutoItemHotbarUseAt = now;
  markItemHotbarUsed(now, ITEM_HOTBAR_AUTO_USE_COOLDOWN_MS);
}

export function combatResourcePercent(current: number, max: number): number {
  if (max <= 0) return 100;
  return (current / max) * 100;
}

export function combatHpPercent(current: number, max: number): number {
  return combatResourcePercent(current, max);
}

export function combatManaPercent(current: number, max: number): number {
  return combatResourcePercent(current, max);
}

/** True while HP% is still below the auto-heal threshold (and not already full). */
export function shouldAutoUseHotbarPotion(
  current: number,
  max: number,
  thresholdPercent: number,
): boolean {
  if (max <= 0) return false;
  if (current >= max) return false;
  return combatHpPercent(current, max) <= thresholdPercent;
}

function pickHealthPotionItemId(economy: HomeEconomy): StashItemId | null {
  for (let index = 0; index < ITEM_HOTBAR_SLOT_COUNT; index += 1) {
    const itemId = economy.itemHotbarSlots[index];
    if (!itemId) continue;
    const tier = tierFromStashItemId(itemId);
    if (!tier || healthPotionCount(economy, tier) <= 0) continue;
    return itemId;
  }
  for (const tier of HEALTH_POTION_TIERS) {
    if (healthPotionCount(economy, tier) > 0) return stashItemIdForTier(tier);
  }
  return null;
}

/** Next HP potion to auto-use (hotbar order, then stash). */
export function pickAutoHotbarPotionSlot(
  economy: HomeEconomy,
): { index: number; itemId: StashItemId } | null {
  const itemId = pickHealthPotionItemId(economy);
  if (!itemId) return null;
  const hotbarIndex = economy.itemHotbarSlots.indexOf(itemId);
  return { index: hotbarIndex >= 0 ? hotbarIndex : 0, itemId };
}

export function shouldAutoUseHotbarManaPotion(
  current: number,
  max: number,
  thresholdPercent: number,
): boolean {
  if (max <= 0) return false;
  if (current >= max) return false;
  return combatManaPercent(current, max) <= thresholdPercent;
}

function pickManaPotionItemId(economy: HomeEconomy): StashItemId | null {
  for (let index = 0; index < ITEM_HOTBAR_SLOT_COUNT; index += 1) {
    const itemId = economy.itemHotbarSlots[index];
    if (!itemId) continue;
    const tier = tierFromManaStashItemId(itemId);
    if (!tier || manaPotionCount(economy, tier) <= 0) continue;
    return itemId;
  }
  for (const tier of MANA_POTION_TIERS) {
    if (manaPotionCount(economy, tier) > 0) return stashItemIdForManaTier(tier);
  }
  return null;
}

/** Next mana potion to auto-use (hotbar order, then stash). */
export function pickAutoHotbarManaPotionSlot(
  economy: HomeEconomy,
): { index: number; itemId: StashItemId } | null {
  const itemId = pickManaPotionItemId(economy);
  if (!itemId) return null;
  const hotbarIndex = economy.itemHotbarSlots.indexOf(itemId);
  return { index: hotbarIndex >= 0 ? hotbarIndex : 0, itemId };
}

const AUTO_HEAL_MAX_STEPS = ITEM_HOTBAR_SLOT_COUNT * 32;

/**
 * While HP is below threshold, use the leftmost eligible hotbar slot each step
 * until threshold is met or no potions remain.
 */
export function runAutoHotbarHealSteps(
  thresholdPercent: number,
  getHp: () => { current: number; max: number },
  pickSlot: () => { index: number; itemId: StashItemId } | null,
  useSlot: (itemId: StashItemId) => boolean,
): number {
  let steps = 0;
  for (let guard = 0; guard < AUTO_HEAL_MAX_STEPS; guard += 1) {
    const { current, max } = getHp();
    if (!shouldAutoUseHotbarPotion(current, max, thresholdPercent)) break;
    const pick = pickSlot();
    if (!pick) break;
    const used = useSlot(pick.itemId);
    if (!used) break;
    steps += 1;
  }
  return steps;
}

export function runAutoHotbarManaSteps(
  thresholdPercent: number,
  getMana: () => { current: number; max: number },
  pickSlot: () => { index: number; itemId: StashItemId } | null,
  useSlot: (itemId: StashItemId) => boolean,
): number {
  let steps = 0;
  for (let guard = 0; guard < AUTO_HEAL_MAX_STEPS; guard += 1) {
    const { current, max } = getMana();
    if (!shouldAutoUseHotbarManaPotion(current, max, thresholdPercent)) break;
    const pick = pickSlot();
    if (!pick) break;
    const used = useSlot(pick.itemId);
    if (!used) break;
    steps += 1;
  }
  return steps;
}

/** One auto potion per call (HP first, then mana) — respects auto cooldown outside. */
export function tryAutoHotbarPotionOnce(
  hpThresholdPercent: number,
  getHp: () => { current: number; max: number },
  pickHp: () => { index: number; itemId: StashItemId } | null,
  manaThresholdPercent: number,
  getMana: () => { current: number; max: number },
  pickMana: () => { index: number; itemId: StashItemId } | null,
  usePotion: (itemId: StashItemId) => boolean,
): boolean {
  const { current: hpCurrent, max: hpMax } = getHp();
  if (shouldAutoUseHotbarPotion(hpCurrent, hpMax, hpThresholdPercent)) {
    const pick = pickHp();
    if (pick && usePotion(pick.itemId)) return true;
  }
  const { current: manaCurrent, max: manaMax } = getMana();
  if (shouldAutoUseHotbarManaPotion(manaCurrent, manaMax, manaThresholdPercent)) {
    const pick = pickMana();
    if (pick && usePotion(pick.itemId)) return true;
  }
  return false;
}
